import React, { useEffect, useState } from "react";
import useFetch from "@/lib/useFetch";
import api, { apiError, money } from "@/lib/api";
import { toast } from "sonner";
import { Layers, Clock, ArrowDownToLine, X, Lock, CheckCircle2, TrendingUp, History, Trophy } from "lucide-react";

const STATUS_STYLE = {
  "ROI ACTIVE": { c: "#10b981", bg: "rgba(16,185,129,0.12)", label: "ROI ACTIVE", icon: TrendingUp },
  "ROI COMPLETED": { c: "var(--gold)", bg: "rgba(201,154,18,0.14)", label: "ROI COMPLETED", icon: CheckCircle2 },
  "QUALIFICATION PENDING": { c: "#f59e0b", bg: "rgba(245,158,11,0.12)", label: "QUALIFICATION PENDING", icon: Clock },
  "ROI EXPIRED": { c: "#ef4444", bg: "rgba(239,68,68,0.12)", label: "ROI EXPIRED", icon: Lock },
  "INCOMPLETE": { c: "#94a3b8", bg: "rgba(148,163,184,0.12)", label: "INCOMPLETE", icon: Lock },
  "INACTIVE / LOCKED": { c: "#f59e0b", bg: "rgba(245,158,11,0.12)", label: "INACTIVE — LOCKED", icon: Lock },
  "NOT JOINED": { c: "#94a3b8", bg: "rgba(148,163,184,0.12)", label: "NOT JOINED", icon: Lock },
  "LOCKED": { c: "#94a3b8", bg: "rgba(148,163,184,0.12)", label: "LOCKED", icon: Lock },
};

function countdown(endIso, now) {
  if (!endIso) return null;
  const ms = new Date(endIso).getTime() - now;
  if (ms <= 0) return "00:00:00";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

function PoolCard({ p, now, onWithdraw, onHistory }) {
  const st = STATUS_STYLE[p.roi_status] || STATUS_STYLE.LOCKED;
  const Icon = st.icon;
  const showTimer = p.pool_id !== 1;
  const timerTarget = !showTimer ? null : (p.qual_status === "pending" || p.roi_status === "ROI EXPIRED" ? p.window_end : (p.roi_status === "ROI ACTIVE" ? p.roi_end_at : null));
  const cd = countdown(timerTarget, now);
  const directWindow = p.direct_business_required > 0;
  const expired = p.roi_status === "ROI EXPIRED";
  const blink = expired || (!!cd && cd !== "00:00:00");
  let condition = `${p.roi_days}-day payout`;
  if (p.roi_status === "QUALIFICATION PENDING") condition = `Complete ${money(p.direct_business_required, 0)} direct business within 7 days`;
  else if (p.roi_status === "ROI EXPIRED") condition = "Direct business condition expired";
  else if (p.roi_status === "INACTIVE / LOCKED") condition = "Activate your ID";
  else if (p.roi_status === "ROI COMPLETED") condition = "Payout complete";
  else if (p.roi_status === "INCOMPLETE" || p.roi_status === "NOT JOINED") condition = `${p.roi_days}d ROI after fill`;
  else if (p.roi_status === "ROI ACTIVE") condition = `${p.roi_days}-day payout running`;
  return (
    <div className={`relative overflow-hidden rounded-2xl p-5 ${expired ? "ring-2 ring-red-500/70 animate-pulse" : ""}`}
      style={{ background: "linear-gradient(160deg,#0f1613,#0a0f0c)", border: "1px solid rgba(34,197,94,0.35)", boxShadow: "0 16px 40px -10px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.12)" }}
      data-testid={`pool-card-${p.pool_id}`}>
      <div className="mb-3 flex items-start justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-[var(--gold)]">Global Pool</p>
          <h3 className="font-display text-2xl font-extrabold text-white">POOL {p.pool_id}</h3>
        </div>
        <span className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: "rgba(34,197,94,0.18)" }}>
          <Layers size={20} className="text-[var(--gold)]" />
        </span>
      </div>

      <div className="flex items-end justify-between">
        <p className="font-display text-3xl font-extrabold text-white" data-testid={`pool-progress-${p.pool_id}`}>
          {p.total_units} <span className="text-lg text-white/55">/ {p.required}</span>
        </p>
        <span className="rounded-full px-2.5 py-1 text-[10px] font-bold" style={{ color: st.c, background: st.bg }} data-testid={`pool-status-${p.pool_id}`}>
          <Icon size={11} className="mr-1 inline" />{st.label}
        </span>
      </div>
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white/15">
        <div className="h-full rounded-full" style={{ width: `${p.progress_pct}%`, background: "linear-gradient(90deg,#C99A12,#f0cf6b)" }} />
      </div>

      <div className="mt-4 rounded-xl px-4 py-3" style={{ background: "rgba(34,197,94,0.10)", border: "1px solid rgba(34,197,94,0.22)" }}>
        <p className="text-[11px] uppercase tracking-wider text-[var(--gold)]">Total Profit</p>
        <p className="font-display text-2xl font-extrabold text-white" data-testid={`pool-profit-${p.pool_id}`}>
          ${money(p.roi_total)} <span className="text-sm font-semibold text-white/75">in {p.roi_days} days</span>
        </p>
        <p className="mt-1 text-[12px] font-bold text-[var(--gold)]" data-testid={`pool-daily-${p.pool_id}`}>≈ ${money(p.daily_roi)} / day ROI</p>
      </div>

      {p.direct_business_required > 0 && (
        <div className="mt-3" data-testid={`pool-biz-progress-${p.pool_id}`}>
          <div className="mb-1 flex justify-between text-[11px] text-white/85">
            <span>Direct Business</span>
            <span className="font-bold text-[var(--gold)]">${money(p.direct_business, 0)} / ${money(p.direct_business_required, 0)}</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-black/30">
            <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, (p.direct_business / p.direct_business_required) * 100)}%`, background: "linear-gradient(90deg,#C99A12,#f0cf6b)" }} />
          </div>
        </div>
      )}

      <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
        <div><p className="text-white/70">Accrued</p><p className="text-sm font-bold" style={{ color: "#f0cf6b" }}>${money(p.roi_accrued)}</p></div>
        <div><p className="text-white/70">Remaining</p><p className="text-sm font-bold" style={{ color: "#ffffff" }}>${money(p.roi_remaining)}</p></div>
        <div><p className="text-white/70">Balance</p><p className="text-sm font-bold" style={{ color: "#ffffff" }}>${money(p.balance)}</p></div>
      </div>

      <div className={`mt-3 grid ${showTimer ? "grid-cols-2" : "grid-cols-1"} gap-2`}>
        <span className="flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-[11px] font-semibold"
          style={{ color: "#fff", background: "rgba(0,0,0,0.34)", border: "1px solid rgba(34,197,94,0.30)" }}
          data-testid={`pool-condition-${p.pool_id}`}>
          <Lock size={12} className="text-[var(--gold)]" /> <span style={{ color: "#fff" }}>{condition}</span>
        </span>
        {showTimer && (
          <span className={`flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-[11px] font-bold ${blink ? "animate-pulse" : ""}`}
            style={{ color: "#fff", background: blink ? "rgba(239,68,68,0.28)" : "rgba(0,0,0,0.34)", border: `1px solid ${blink ? "rgba(239,68,68,0.6)" : "rgba(34,197,94,0.30)"}` }}
            data-testid={`pool-timer-${p.pool_id}`}>
            <Clock size={12} className={expired ? "text-red-300" : "text-[var(--gold)]"} /> <span style={{ color: "#fff" }}>{directWindow && (p.qual_status === "pending" || expired) ? (cd || "00:00:00") : (cd || "--:--:--")}</span>
          </span>
        )}
      </div>

      {expired && (
        <div className="mt-3 rounded-xl border border-red-500/50 bg-red-500/10 px-3 py-2 text-center text-[11px] font-semibold text-red-200" data-testid={`pool-expired-reason-${p.pool_id}`}>
          ⚠️ You did not complete the ${money(p.direct_business_required, 0)} direct business condition within 168 hours (7 days). This pool income has expired.
        </div>
      )}
      <div className="mt-3 flex items-center gap-2">
        <button onClick={() => onWithdraw(p)} disabled={p.balance <= 0 || !p.is_active || !["ROI ACTIVE","ROI COMPLETED"].includes(p.roi_status)}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl btn3d btn3d-pool-withdraw px-3 py-2.5 text-sm ${p.balance > 0 && p.is_active && ["ROI ACTIVE","ROI COMPLETED"].includes(p.roi_status) ? "animate-pulse ring-2 ring-emerald-300" : "opacity-50 cursor-not-allowed"}`}
          title={!p.is_active ? "Activate your ID to withdraw" : p.roi_status === "QUALIFICATION PENDING" ? "Complete the 7-day direct business condition first" : p.roi_status === "ROI EXPIRED" ? "This pool income has expired" : "No withdrawable ROI"}
          data-testid={`pool-withdraw-btn-${p.pool_id}`}>
          <ArrowDownToLine size={15} /> Withdraw{p.balance > 0 ? ` ${money(p.balance)}` : ""}
        </button>
        <button onClick={() => onHistory(p)} className="grid place-items-center rounded-xl btn3d px-3 py-2.5"
          style={{ background: "rgba(0,0,0,0.34)", border: "1px solid rgba(34,197,94,0.32)", color: "#F4C430" }} title="ROI history" data-testid={`pool-history-btn-${p.pool_id}`}>
          <History size={15} />
        </button>
      </div>
    </div>
  );
}

export default function GlobalPools() {
  const { data, loading, reload } = useFetch("/user/global-pools");
  const { data: lb } = useFetch("/user/pool-leaderboard");
  const [now, setNow] = useState(Date.now());
  const [wd, setWd] = useState(null);
  const [withdrawStep, setWithdrawStep] = useState(1);
  const [form, setForm] = useState({ amount: "", wallet_address: "" });
  const [busy, setBusy] = useState(false);
  const [hist, setHist] = useState(null);
  const [histData, setHistData] = useState(null);
  const [poolPopup, setPoolPopup] = useState(null);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let alive = true;
    const checkPoolNotifications = async () => {
      try {
        const r = await api.get("/user/notifications");
        const notes = (r.data?.notifications || []).filter((n) => n.kind === "pool_completed" && n.read === false);
        if (alive && notes.length) {
          setPoolPopup(notes[notes.length - 1]);
          reload();
        }
        if (notes.length) await api.post("/user/notifications/pool-completed/read");
      } catch (_) {}
    };
    checkPoolNotifications();
    const id = setInterval(checkPoolNotifications, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    if (!hist) { setHistData(null); return; }
    api.get(`/user/global-pools/${hist.pool_id}/history`).then((r) => setHistData(r.data)).catch(() => setHistData({ roi: [], withdrawals: [] }));
  }, [hist]);

  const pools = data?.pools || [];

  const submit = async () => {
    if (!form.wallet_address.trim()) { toast.error("Please enter your USDT BEP20 wallet address"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/user/global-pools/${wd.pool_id}/withdraw`, {
        amount: parseFloat(form.amount), wallet_address: form.wallet_address, coin: "USDT", network: "BEP20",
      });
      toast.success(`Requested — net $${money(r.data.net)} USDT`);
      setWd(null); setWithdrawStep(1); setForm({ amount: "", wallet_address: "" }); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="global-pool-section">
      <div className="mb-3 flex items-center gap-2">
        <Layers size={20} className="text-[var(--gold)]" />
        <h2 className="font-display text-xl font-bold text-white">Global Pool</h2>
        <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] text-white/50">7 independent pools · daily ROI payouts</span>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => <div key={i} className="skeleton h-64 rounded-2xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {pools.map((p) => <PoolCard key={p.pool_id} p={p} now={now} onWithdraw={(pp) => { if (pp.balance <= 0) { toast.info("No withdrawable ROI in this pool yet — it appears here once daily ROI accrues."); return; } setWd(pp); setWithdrawStep(1); setForm({ amount: "", wallet_address: "" }); }} onHistory={(pp) => setHist(pp)} />)}
        </div>
      )}

      {poolPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="pool-completed-popup-overlay" role="dialog" aria-modal="true">
          <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-[var(--gold)]/60 bg-[#07140d] p-7 text-center shadow-[0_25px_80px_rgba(0,0,0,0.65)]" data-testid="pool-completed-popup">
            <button type="button" onClick={() => setPoolPopup(null)} aria-label="Close" className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white" data-testid="pool-completed-popup-close">×</button>
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-[var(--gold)]/50 bg-[var(--gold)]/10 text-3xl">🎉</div>
            <h2 className="mt-4 font-display text-2xl font-extrabold text-[var(--gold)]">Congratulations!</h2>
            <p className="mt-3 text-sm leading-6 text-white/80">{poolPopup.body}</p>
            <button type="button" onClick={() => setPoolPopup(null)} className="mt-6 inline-flex min-w-32 items-center justify-center rounded-xl bg-[var(--gold)] px-6 py-2.5 text-sm font-extrabold text-[#07140d] hover:brightness-110" data-testid="pool-completed-popup-ok">Close</button>
          </div>
        </div>
      )}

      {wd && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onClick={() => setWd(null)}>
          <div className="w-full max-w-md rounded-2xl p-6" style={{ background: "linear-gradient(160deg,#0f1613,#0a0f0c)", border: "1px solid rgba(34,197,94,0.25)" }} onClick={(e) => e.stopPropagation()} data-testid="pool-withdraw-modal">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--gold)]">Global Pool Withdrawal</p>
                <h3 className="font-display text-lg font-bold text-white">Pool {wd.pool_id} — Step {withdrawStep} of 2</h3>
              </div>
              <button onClick={() => { setWd(null); setWithdrawStep(1); }} className="text-white/60 hover:text-white"><X size={18} /></button>
            </div>
            {withdrawStep === 1 ? (
              <>
                <p className="mb-4 text-sm text-white/60">Available in Pool {wd.pool_id}: <b className="gold-text">${money(wd.balance)}</b></p>
                <label className="mb-2 block text-xs font-semibold text-white/70">Withdrawal Amount</label>
                <input type="number" min="10" step="10" placeholder="Enter amount in USDT" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="mb-3 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[var(--gold)]/50" data-testid="pool-withdraw-amount" />
                <p className="mb-4 text-[11px] text-white/45">Minimum $10 · multiples of $10 · 10% withdrawal fee</p>
                <button onClick={() => {
                  const amount = parseFloat(form.amount);
                  if (!Number.isFinite(amount) || amount <= 0) { toast.error("Enter a valid withdrawal amount"); return; }
                  if (amount < 10) { toast.error("Minimum withdrawal is $10"); return; }
                  if (amount % 10 !== 0) { toast.error("Withdrawal amount must be in multiples of $10"); return; }
                  if (amount > Number(wd.balance)) { toast.error("Amount exceeds this pool balance"); return; }
                  setWithdrawStep(2);
                }} className="w-full rounded-xl btn3d-gold py-3 font-bold text-black" data-testid="pool-withdraw-continue">Continue</button>
              </>
            ) : (
              <>
                <div className="mb-4 rounded-xl border border-[var(--gold)]/25 bg-[var(--gold)]/5 p-3">
                  <p className="text-xs text-white/55">Withdrawal Amount</p>
                  <p className="text-lg font-bold text-[var(--gold)]">${money(parseFloat(form.amount || 0))} USDT</p>
                </div>
                <label className="mb-2 block text-xs font-semibold text-white/70">USDT BEP20 Withdrawal Address</label>
                <input autoFocus placeholder="Paste your BEP20 (BSC) USDT wallet address" value={form.wallet_address}
                  onChange={(e) => setForm({ ...form, wallet_address: e.target.value.trim() })}
                  className="mb-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[var(--gold)]/50" data-testid="pool-withdraw-wallet" />
                <p className="mb-4 text-[11px] text-white/45">Use a valid BSC / BEP20 address starting with 0x. Do not paste an exchange deposit memo/tag here.</p>
                <div className="flex gap-2">
                  <button onClick={() => setWithdrawStep(1)} disabled={busy} className="flex-1 rounded-xl border border-white/15 bg-white/5 py-3 font-semibold text-white/80" data-testid="pool-withdraw-back">Back</button>
                  <button onClick={submit} disabled={busy || !form.wallet_address.trim()} className="flex-[2] rounded-xl btn3d-gold py-3 font-bold text-black disabled:opacity-50" data-testid="pool-withdraw-submit">
                    {busy ? "Processing..." : "Submit Withdrawal"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {hist && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onClick={() => setHist(null)}>
          <div className="w-full max-w-lg rounded-2xl p-6" style={{ background: "linear-gradient(160deg,#0f1613,#0a0f0c)", border: "1px solid rgba(34,197,94,0.25)" }} onClick={(e) => e.stopPropagation()} data-testid="pool-history-modal">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-white">Pool {hist.pool_id} — ROI History</h3>
              <button onClick={() => setHist(null)} className="text-white/60 hover:text-white"><X size={18} /></button>
            </div>
            {!histData ? <div className="skeleton h-40" /> : (
              <div className="max-h-[60vh] space-y-4 overflow-y-auto">
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--gold)]">Daily ROI Accrual</p>
                  {histData.roi?.length ? (
                    <table className="w-full text-sm" data-testid="pool-roi-history-table">
                      <thead className="text-left text-white/45"><tr className="border-b border-white/10"><th className="py-2">Date</th><th className="py-2">Status</th><th className="py-2 text-right">Amount</th></tr></thead>
                      <tbody>
                        {histData.roi.map((e, i) => (
                          <tr key={i} className="border-b border-white/5">
                            <td className="py-2 text-white/80">{e.date}</td>
                            <td className="py-2 capitalize text-white/50">{e.status}</td>
                            <td className="py-2 text-right font-medium gold-text">+${money(e.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <p className="text-sm text-white/40">No ROI credited yet for this pool.</p>}
                </div>
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--gold)]">Withdrawals</p>
                  {histData.withdrawals?.length ? (
                    <table className="w-full text-sm">
                      <thead className="text-left text-white/45"><tr className="border-b border-white/10"><th className="py-2">ID</th><th className="py-2">Net</th><th className="py-2">Status</th><th className="py-2">Date</th></tr></thead>
                      <tbody>
                        {histData.withdrawals.map((w) => (
                          <tr key={w.pw_id} className="border-b border-white/5">
                            <td className="py-2 text-white/80">{w.pw_id}</td>
                            <td className="py-2 gold-text">${money(w.net)}</td>
                            <td className="py-2 capitalize text-white/60">{w.status}</td>
                            <td className="py-2 text-white/50">{(w.created_at || "").slice(0, 10)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <p className="text-sm text-white/40">No withdrawals from this pool yet.</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
